const privacyPolicyPage = {
  label: "Privacy Policy",
  title: "Solstice Diary Privacy Policy",
  metadata: [
    { label: "Effective date", value: "August 13, 2026" },
    { label: "Operator", value: "Nathaniel James Markram" },
    {
      label: "Privacy contact",
      value: "hello@solsticediary.com",
      href: "mailto:hello@solsticediary.com",
    },
    {
      label: "Mailing address",
      value: "13 St James St, Audas Estate, Cape Town, 7130",
    },
  ],
  introParagraphs: [
    "We know journaling can hold some of the most personal parts of your life. If you are trusting Solstice Diary with that kind of information, you deserve a clear explanation of how we handle it.",
    "This policy explains what we collect, why we store it, how AI features fit in, and what choices you have. We try to write it in plain language while staying accurate about how the product works today.",
  ],
  sections: [
    {
      heading: "1. Welcome",
      paragraphs: [
        "Solstice Diary is built to help you reflect, notice patterns, and return to important moments over time. That only works if the app feels trustworthy.",
        "Some parts of the app work only on your device, and some rely on cloud infrastructure so your account, saved history, AI tools, and search features can work. We want to be straightforward about that from the start.",
      ],
    },
    {
      heading: "2. Our Commitment",
      paragraphs: [
        "We treat journal content as sensitive and try to be careful both in how we handle it and in how we describe our practices.",
      ],
      bullets: [
        "We do not sell your journal content to advertisers.",
        "Much of your saved journal content is encrypted on your device before it is stored in Firestore.",
        "We try to explain our data practices in plain language and keep app-store disclosures aligned with this policy.",
        "We do not describe Solstice Diary as end-to-end encrypted because backend AI features require server-side processing of some content.",
      ],
    },
    {
      heading: "3. Why We Store Data",
      paragraphs: [
        "Not everything is stored in the same place. Some information stays only on your device, while other information is stored in Firebase services because core parts of the product depend on it.",
      ],
      bullets: [
        "We store account and saved-content records in the cloud so you can sign in securely, keep your history across devices, and restore access if you reinstall the app or change phones.",
        "We store saved entries, conversations, goals, events, and settings so the product can synchronize your data and preserve the state of your journal over time. Saved entries and conversations are encrypted before they are stored in Firestore.",
        "We create derived records, such as memories, search indexes, and saved reports, so AI-assisted search, summaries, and reflection features can work more reliably and quickly.",
        "We keep certain data only on your device, such as local reminder schedules, cached settings, and encrypted unsaved drafts, because those features do not always need cloud storage.",
      ],
    },
    {
      heading: "4. Who We Are",
      paragraphs: [
        "Solstice Diary is operated by Nathaniel James Markram.",
        "For the purposes of applicable data protection law, Nathaniel James Markram is the data controller of the personal information described in this Privacy Policy, unless stated otherwise.",
        "You can contact him about privacy questions, rights requests, or complaints using the contact details listed at the top of this policy.",
      ],
    },
    {
      heading: "5. Eligibility",
      paragraphs: [
        "Solstice Diary is intended only for users who are 18 years of age or older and is not directed to children.",
        "We do not knowingly collect personal information from anyone under 18.",
        "If you believe someone under 18 has provided personal information through the app, contact us so we can review the issue and take appropriate action.",
      ],
    },
    {
      heading: "6. Information We Collect",
      paragraphs: [
        "The information we collect depends on how you use the app. Some information is stored locally on your device, some is stored in Firebase services, and some is processed through backend AI features.",
        "Because Solstice Diary is a journaling and reflection app, the content you choose to enter may include sensitive personal information, including information about your emotions, relationships, routines, beliefs, or wellbeing.",
      ],
      bullets: [
        "Account and authentication data, such as your email address, password credentials handled through Firebase Authentication, password-reset activity, and basic account/session status.",
        "Profile and preference data, such as your display name, selected chat persona, archetype or persona dimensions, onboarding status, onboarding version, theme, day-end settings, week-start preference, calendar display settings, intro-completion flags, and similar app settings.",
        "Journal and reflection content that you choose to provide, such as guided chat messages, saved conversation transcripts, free-writing entries, titles, summaries, analysis text, structured analysis items, insights, mood overrides, pinned insights, and user-selected tags.",
        "Goals and habit data, such as goal titles, optional reasons or descriptions, emojis, repeat settings, reminder slots, archived or completed status, counters, progress records, and completion timestamps.",
        "Calendar and event data, such as event titles, notes, location text, birthdays, all-day flags, start and end times, recurrence settings, reminder offsets, timezone names, and timezone offsets.",
        "Search and navigation inputs, such as semantic search queries, tag filters, selected date ranges, report periods, and drill-down selections used inside the app.",
        "Subscription and purchase data, such as your Firebase account identifier supplied to RevenueCat as an app user ID, subscription status, entitlement status, product and offering identifiers, purchase and renewal dates, and transaction or receipt information handled by RevenueCat and the Apple App Store or Google Play.",
        "Product analytics data, such as app opens, onboarding milestones, chat-session starts, entry saves, report generation, paywall activity, subscription restoration, app version, build number, and build environment. These analytics events are designed not to include journal text, message text, event titles, goal titles, or other free-form journal content.",
        "Diagnostics and crash data, such as errors, stack traces, app and device metadata, sanitized technical context, feature and action labels, and a hashed account identifier used to investigate reliability problems without placing your raw Firebase user ID in Crashlytics.",
        "Website waitlist data, including the email address and source value you submit, together with ordinary request information that Formspree or our hosting providers may process, such as IP address, browser or device information, access time, and server logs.",
        "Local-only data stored on your device, including encrypted unsaved chat drafts, lightweight draft metadata, local reminder payloads, reminder times, and related device permission states for notifications and exact alarms, as described further in Section 7.",
      ],
    },
    {
      heading: "7. Local Device Storage",
      paragraphs: [
        "The current app code stores some information only on your device to support encryption, drafts, settings, and local reminders.",
      ],
      bullets: [
        "A per-account content-encryption key is cached locally in secure device storage after it is provisioned to an authenticated device by the Solstice Diary backend.",
        "Unsaved chat drafts are stored locally in encrypted form, along with small unencrypted draft metadata such as draft IDs, titles, and timestamps.",
        "Settings and UI preferences are cached locally to support offline access and faster startup.",
        "Event and goal reminders are scheduled locally on the device rather than as cloud push notifications in the current app code.",
        "Depending on your device settings, reminder content may appear on your lock screen or in local notification previews.",
      ],
    },
    {
      heading: "8. Cloud Storage and Derived Data",
      paragraphs: [
        "When you are signed in, Solstice Diary stores data in Firebase services. In addition to the records you create directly, we may also create derived records to support AI features, search, and reports.",
      ],
      bullets: [
        "Primary cloud records may include user profile documents, journal entries, conversation records, days or mood records, goals, goal counters, goal progress records, events, app settings, and saved insights reports.",
        "Derived AI records may include memory records, memory indexes, entry search indexes, and report artifacts generated from your saved content.",
        "Operational records may include pseudonymous per-user AI usage totals, abuse-prevention and rate-limit records, subscription entitlement status, product analytics events, and crash diagnostics.",
        "When you delete an entry, we are designed to also remove related memory and search records created from that entry.",
        "When you permanently delete your account in-app, the deletion flow is designed to remove your core Firestore account tree, backend encryption key, per-user backend AI telemetry and rate-limit records, and Firebase Authentication account. Section 14 explains records that may remain with service providers, in backups, or in aggregated form.",
      ],
    },
    {
      heading: "9. AI Features and Automated Processing",
      paragraphs: [
        "Solstice Diary routes AI-powered features through Firebase Cloud Functions. OpenAI and Anthropic provide AI text generation, OpenAI also supports safety moderation, and Cohere creates semantic embeddings used for search and contextual retrieval. These providers may process the content you submit and the related context needed to return the requested feature.",
        "The current codebase supports AI chat replies, structured summaries, adaptive openers, chat suggestions, insights reports, goal suggestions, emoji suggestions, semantic search embeddings, and long-term memory extraction from saved content.",
      ],
      bullets: [
        "For guided chat and reflection features, the backend may process recent messages, selected mode, selected persona data, and relevant memory context to generate replies or suggestions.",
        "For saved journal content, the backend may decrypt relevant stored content to generate summaries, insights, reports, memory records, and search indexes.",
        "For semantic search and contextual retrieval, the backend may create embeddings or other derived representations from decrypted summaries and extracted memory statements.",
        "For goal suggestions and emoji suggestions, the backend may process goal titles, entry summaries, analysis text, tags, and recent memory context.",
        "We do not send your raw Firebase user ID or email address as your identity to the AI providers. OpenAI and Anthropic requests use a salted pseudonymous safety identifier, while Cohere embedding requests do not include your raw account identifier. The text itself may still contain identifying details that you wrote.",
        "These AI features can influence what content is shown back to you, but they are not a substitute for professional medical, legal, or financial advice.",
      ],
    },
    {
      heading: "10. How We Use Personal Information",
      paragraphs: [
        "We use personal information to operate, secure, and provide Solstice Diary, to deliver the features you request, and to comply with legal obligations.",
      ],
      bullets: [
        "Create and manage your account, authenticate sessions, and support password resets.",
        "Save, display, edit, search, organize, and delete journal entries, conversations, moods, goals, events, and settings.",
        "Generate AI-assisted replies, summaries, reports, suggestions, search results, and related derived records that support app features.",
        "Measure limited product usage, subscription and paywall activity, and service performance without intentionally placing journal text in analytics events.",
        "Diagnose crashes, errors, and reliability problems using sanitized technical information and a hashed account identifier.",
        "Verify, restore, and manage subscription access through RevenueCat and the relevant app store.",
        "Receive and manage website waitlist submissions and send early-access or launch communications requested through the website.",
        "Sync your settings and content across your signed-in devices.",
        "Schedule and manage local device reminders for events and goals.",
        "Maintain security, troubleshoot problems, respond to support requests, and protect our legal rights.",
      ],
    },
    {
      heading: "11. How We Share Information",
      paragraphs: [
        "We do not sell your journal content to advertisers. We share personal information only when needed to operate the service, when you ask us to, or when required by law.",
      ],
      bullets: [
        "Google and Firebase, which provide authentication, Firestore, Cloud Functions, App Check, Analytics, Crashlytics, and hosting or related infrastructure.",
        "OpenAI, which processes certain prompts and context for AI generation and safety moderation.",
        "Anthropic, which processes certain prompts and context for AI generation.",
        "Cohere, which processes selected summaries, memory statements, and search text to create semantic embeddings.",
        "RevenueCat, which receives an app user ID based on your Firebase account identifier and processes subscription entitlements, product information, device or SDK information, and app-store transaction or receipt data.",
        "Apple and Google, which operate the app stores, process purchases and refunds, and may provide transaction and subscription information needed to deliver paid access.",
        "Formspree, which processes the email address, source value, and ordinary technical request data submitted through the website waitlist form.",
        "Your device operating system and apps you choose, when you use sharing features to send content outside Solstice Diary.",
        "Service providers, professional advisors, regulators, law enforcement, or parties involved in a merger, acquisition, financing, or sale of assets, where disclosure is reasonably necessary.",
        "We do not intentionally include third-party mobile advertising SDKs, sell journal content to advertisers, or use product analytics and crash information for advertising.",
      ],
    },
    {
      heading: "12. Security and Encryption",
      paragraphs: [
        "Much of your saved journal content is encrypted on your device before it is stored in Firestore. The per-account encryption key is cached in secure device storage and is also held by the authenticated Solstice Diary backend so it can be provisioned to your other signed-in devices and used by authorized backend functions.",
        "To provide features such as AI summaries, memories, search, Story, and insights, authorized backend functions may retrieve the key, decrypt relevant content on our servers, send needed text and context to the applicable AI provider, and create related derived records.",
        "Because these features require server-side processing, Solstice Diary is not end-to-end encrypted. We use administrative, technical, and organizational safeguards designed to help protect personal information, but no method of storage or transmission is completely secure.",
      ],
    },
    {
      heading: "13. International Transfers",
      paragraphs: [
        "We and our service providers may process or store personal information in countries other than the country where you live. Those countries may have different data-protection laws.",
        "Where applicable, we rely on appropriate safeguards and contractual measures that are intended to support lawful transfers of personal information.",
      ],
    },
    {
      heading: "14. Retention",
      paragraphs: [
        "We generally keep personal information for as long as your account remains active, as long as needed to provide the features you use, or as long as needed for legitimate business or legal purposes.",
        "If you delete content or delete your account, we will aim to remove the relevant Solstice-controlled active records in the ordinary course of operation. The in-app account-deletion flow is designed to remove the core Firestore account tree, backend encryption key, per-user backend AI telemetry and rate-limit records, and Firebase Authentication account.",
        "RevenueCat, Apple, Google, Firebase Analytics, Firebase Crashlytics, OpenAI, Anthropic, Cohere, Formspree, and other providers may retain transaction, diagnostics, security, request, or service records under their own legal obligations and retention practices. Aggregated statistics that no longer identify or relate back to your account may also remain.",
        "Residual copies may remain for a limited period in backups or recovery systems and will be deleted in the normal course; this policy does not promise a specific backup-deletion window or override a provider's legally required retention.",
        "Device-local data, such as drafts, settings caches, reminder schedules, and locally stored encryption keys, may remain on your device until removed by the app, by sign-out or cleanup flows, by deleting the app, or by clearing device storage.",
      ],
    },
    {
      heading: "15. Your Rights and Choices",
      paragraphs: [
        "Depending on where you live, you may have rights to request access to, correction of, deletion of, restriction of, objection to, or portability of certain personal information. You may also have the right to complain to a regulator.",
      ],
      bullets: [
        "Account and content management: you can edit or delete certain entries, goals, events, and settings in the app.",
        "Entry deletion: deleting an entry is designed to remove the entry and associated derived memory and search cleanup records tied to that entry.",
        "Account deletion: the app includes an in-app permanent account deletion flow.",
        "Subscriptions: you can manage or cancel an app-store subscription through Apple or Google and can use the app's purchase-restoration or subscription-management tools where available.",
        "Analytics and diagnostics: you may have device, platform, or legal rights relating to analytics and diagnostics. Contact us for help with a verified request; some provider-held or aggregate records cannot be removed through the in-app account-deletion function.",
        "Website waitlist: you may ask us to remove your waitlist email or use the unsubscribe method included in a communication. Deleting an app account does not automatically identify and remove a separate Formspree waitlist submission.",
        "Notifications: you can control local reminder permissions through your device settings and, on supported devices, exact-alarm permissions.",
        "Sharing: any use of the device share sheet is optional and controlled by you.",
        "Rights requests: contact us at hello@solsticediary.com. We may need to verify your identity before completing certain requests.",
      ],
    },
    {
      heading: "16. EEA, UK, and Similar Legal Bases",
      paragraphs: [
        "If data-protection laws in your jurisdiction require a legal basis, we generally rely on one or more of the following: performance of a contract with you, your consent, our legitimate interests, and compliance with legal obligations.",
      ],
      bullets: [
        "Performance of a contract: providing your account, syncing content, saving entries, and delivering requested app features.",
        "Consent: where required, for example for certain optional permissions or where local law requires consent for a particular processing activity.",
        "Legitimate interests: improving service reliability, securing the app, preventing abuse, and supporting contextual AI features requested through the product.",
        "Legal obligations: responding to lawful requests, enforcing rights, and meeting compliance duties.",
      ],
    },
    {
      heading: "17. California Notice",
      paragraphs: [
        "If California privacy law applies to our processing of your personal information, California residents may have rights to request access to, correction of, and deletion of certain personal information, subject to exceptions and verification requirements.",
        "We do not knowingly sell personal information, and we do not knowingly share personal information for cross-context behavioral advertising.",
      ],
    },
    {
      heading: "18. Web Pages and Web Hosting",
      paragraphs: [
        "If you visit our website or other public web pages for Solstice Diary, Firebase Hosting and related infrastructure may process basic technical information such as IP address, browser or device information, and server logs as needed to deliver, maintain, and secure those pages.",
        "If you join the waitlist, the website sends your email address and a source value to Formspree. Formspree may also process ordinary request information such as your IP address, browser type, access time, and referring page. We use the submission to provide the early-access and launch updates you requested.",
        "The current website does not intentionally run behavioral analytics or advertising scripts. If we add non-essential cookies, website analytics, advertising, or similar tracking technologies, we will update this Privacy Policy and provide any additional notice or choice required by law.",
      ],
    },
    {
      heading: "19. Changes to This Policy",
      paragraphs: [
        "We may update this Privacy Policy from time to time. If we make material changes, we will post the updated version with a revised effective date and, where appropriate, provide additional notice.",
      ],
    },
    {
      heading: "20. Contact Us",
      paragraphs: [
        "Privacy requests and questions: hello@solsticediary.com",
        "Mailing address: 13 St James St, Audas Estate, Cape Town, 7130",
        "Operator or controller: Nathaniel James Markram",
      ],
    },
  ],
};

const termsOfServicePage = {
  label: "Terms of Service",
  title: "Solstice Diary Terms of Service",
  metadata: [
    { label: "Effective date", value: "August 13, 2026" },
    { label: "Operator", value: "Nathaniel James Markram" },
    {
      label: "Support or legal contact",
      value: "hello@solsticediary.com",
      href: "mailto:hello@solsticediary.com",
    },
    {
      label: "Mailing address",
      value: "13 St James St, Audas Estate, Cape Town, 7130",
    },
    { label: "Governing law", value: "the Republic of South Africa" },
    { label: "Venue", value: "Cape Town, Western Cape, South Africa" },
  ],
  introParagraphs: [
    "These Terms of Service govern your access to and use of Solstice Diary, including the mobile app, related websites, and backend services we operate for the product.",
    "By creating an account, accessing, or using Solstice Diary, you agree to these Terms. If you do not agree, do not use the service.",
  ],
  sections: [
    {
      heading: "1. Who We Are",
      paragraphs: [
        "Solstice Diary is operated by Nathaniel James Markram.",
        "These Terms are between you and Nathaniel James Markram, and not with Apple, Google, or any other app store operator.",
        "You can contact us using the details listed at the top of these Terms.",
      ],
    },
    {
      heading: "2. Eligibility",
      paragraphs: [
        "Solstice Diary is intended only for users who are 18 years of age or older. By using the service, you represent that you are at least 18 and are legally able to enter into a binding agreement.",
        "If you are using the service on behalf of an organization, you represent that you have authority to bind that organization to these Terms.",
      ],
    },
    {
      heading: "3. Your Account",
      paragraphs: [
        "Some features require an account. You agree to provide accurate information, keep your credentials secure, and promptly update your account information if it changes.",
        "You are responsible for activity that occurs through your account unless you promptly notify us of unauthorized access or a security issue.",
      ],
    },
    {
      heading: "4. License to Use the Service",
      paragraphs: [
        "Subject to these Terms, we grant you a limited, personal, non-exclusive, non-transferable, revocable license to use Solstice Diary for your own lawful, internal use.",
        "This license does not transfer ownership of the app, software, or related intellectual property to you.",
      ],
      bullets: [
        "You may not copy, distribute, sell, lease, sublicense, or commercially exploit the service except as expressly permitted by us in writing.",
        "You may not reverse engineer, decompile, or attempt to extract source code or underlying models, except to the extent that applicable law prohibits that restriction.",
        "You may not use the service to build a competing product or to probe, scan, or test the vulnerability of our systems without authorization.",
      ],
    },
    {
      heading: "5. Your Content",
      paragraphs: [
        "You retain ownership of the journal entries, messages, notes, goals, events, tags, and other content you submit to Solstice Diary.",
        "You grant us a limited, non-exclusive license to host, store, reproduce, transmit, format, process, and otherwise use your content only as necessary to operate, secure, maintain, improve, and provide the service to you, including generating AI-assisted outputs, search indexes, reminders, summaries, and related derived records.",
        "You represent that you have the rights needed to submit your content and that your content does not violate law or the rights of others.",
      ],
    },
    {
      heading: "6. AI Features and Service Limitations",
      paragraphs: [
        "Solstice Diary includes AI-assisted features such as guided chat replies, summaries, reports, suggestions, memory extraction, and search support. AI outputs may be incomplete, inaccurate, or inappropriate for your specific circumstances.",
        "The service is for journaling, reflection, and personal organization. It is not a medical device, therapist, emergency service, or provider of legal, financial, or crisis-response services.",
        "If you believe you are in danger or need urgent help, do not rely on the app; contact local emergency services or a qualified professional.",
      ],
    },
    {
      heading: "7. Acceptable Use",
      paragraphs: [
        "Solstice Diary is a private reflection space. Writing honestly about trauma, distress, abuse, self-harm, difficult emotions, or other painful experiences is not prohibited merely because the subject is sensitive. You may not, however, use the service to facilitate unlawful conduct, exploit or threaten other people, attack the service, or violate another person's rights.",
      ],
      bullets: [
        "Use the service to create, distribute, or facilitate unlawful material, sexual exploitation, fraud, infringement, or instructions intended to cause unlawful harm.",
        "Use the service to stalk, threaten, harass, impersonate, exploit, or deceive another person, or to organize violence or abuse against others.",
        "Interfere with the operation of the service, bypass access controls, or introduce malware, bots, or harmful code.",
        "Present AI-generated material as professional advice or use it to make high-impact decisions for another person without appropriate qualified review.",
        "Use the service in a way that violates applicable law, regulation, or third-party rights.",
      ],
    },
    {
      heading: "8. Privacy",
      paragraphs: [
        "Our Privacy Policy explains how we collect, use, store, and share personal information. By using the service, you acknowledge that your information will be handled as described in the Privacy Policy.",
      ],
    },
    {
      heading: "9. Third-Party Services and App Stores",
      paragraphs: [
        "Solstice Diary relies on third-party services, including Google and Firebase, OpenAI, Anthropic, Cohere, RevenueCat, Formspree, Apple, and Google Play. Your use of third-party platforms, operating system features, or external destinations may also be subject to those third parties' own terms and policies.",
        "If you downloaded the app from an app store, that store may have additional rules that apply to your use of the app. To the maximum extent permitted by law, app store operators are not responsible for the service itself.",
      ],
    },
    {
      heading: "10. Intellectual Property",
      paragraphs: [
        "The service, including the app, software, design, branding, interfaces, and non-user content, is owned by us or our licensors and is protected by intellectual-property and other laws.",
        "Except for the limited license granted to you in these Terms, we reserve all rights not expressly granted.",
      ],
    },
    {
      heading: "11. Feedback",
      paragraphs: [
        "If you send us suggestions, ideas, or feedback about Solstice Diary, you grant us a non-exclusive, worldwide, royalty-free, irrevocable license to use that feedback for any lawful purpose without compensation to you.",
      ],
    },
    {
      heading: "12. Subscriptions and App-Store Billing",
      paragraphs: [
        "Solstice Diary may offer paid, automatically renewing subscriptions through the Apple App Store or Google Play. RevenueCat helps us display offerings, verify entitlement status, restore purchases, and provide subscription-management tools.",
        "Before you confirm a purchase, the app store or paywall will show the applicable product, billing period, localized price and currency, taxes where applicable, and any trial or introductory offer. Those checkout details form part of your purchase terms; Solstice Diary does not promise one fixed price across countries or stores.",
        "Payment is charged by Apple or Google to the payment method connected to your store account. Unless the checkout states otherwise or you cancel in time, an automatically renewing subscription renews for the displayed period at the then-applicable price permitted by the store's rules.",
      ],
      bullets: [
        "Manage or cancel a subscription through the app store account used to purchase it or through the subscription-management destination made available in the app.",
        "Deleting the Solstice Diary app or your Solstice Diary account does not itself cancel an app-store subscription; cancel it separately through Apple or Google.",
        "Purchase restoration is available for eligible purchases, but successful restoration depends on the app store, RevenueCat, the store account, and the Solstice Diary account associated with the entitlement.",
        "Refunds, billing disputes, price changes, trials, and renewal notices are governed by the applicable app store's rules and mandatory consumer law.",
        "Paid access may begin, change, pause, or end when a subscription is purchased, expires, is cancelled, refunded, transferred, or cannot be verified.",
      ],
    },
    {
      heading: "13. Changes to the Service",
      paragraphs: [
        "We may add, remove, suspend, or change features of Solstice Diary at any time. We may also set reasonable limits on use, storage, or access as needed to operate the service.",
        "We may change which features require a subscription or offer additional products, subject to checkout disclosures, app-store requirements, these Terms, and applicable law.",
      ],
    },
    {
      heading: "14. Suspension and Termination",
      paragraphs: [
        "You may stop using the service at any time. We may suspend or terminate your access if we reasonably believe you violated these Terms, created risk or possible legal exposure, or if we need to protect the service, users, or others.",
        "Sections that by their nature should survive termination will survive, including provisions about ownership, disclaimers, limitation of liability, indemnity, disputes, and feedback.",
      ],
    },
    {
      heading: "15. Disclaimers",
      paragraphs: [
        'To the maximum extent permitted by law, Solstice Diary is provided on an "as is" and "as available" basis. We do not guarantee that the service will be uninterrupted, secure, error-free, or that content or AI outputs will always be accurate, complete, or suitable for your needs.',
        "We disclaim all warranties, express or implied, including implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement, except to the extent such disclaimers are not permitted by law.",
      ],
    },
    {
      heading: "16. Limitation of Liability",
      paragraphs: [
        "To the maximum extent permitted by law, we and our affiliates, officers, employees, contractors, licensors, and service providers will not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or for any loss of profits, revenues, goodwill, data, or use, arising out of or related to the service or these Terms.",
        "To the maximum extent permitted by law, our total aggregate liability for all claims arising out of or relating to the service or these Terms will not exceed the greater of: (a) the amount you paid us for the service in the 12 months before the event giving rise to the claim, or (b) USD $100.",
        "Some jurisdictions do not allow certain exclusions or limitations, so some of the limitations above may not apply to you.",
      ],
    },
    {
      heading: "17. Indemnity",
      paragraphs: [
        "To the maximum extent permitted by law, you will indemnify and hold harmless us and our affiliates, officers, employees, contractors, licensors, and service providers from and against claims, liabilities, damages, losses, and expenses arising out of or related to your content, your misuse of the service, or your violation of these Terms or applicable law.",
      ],
    },
    {
      heading: "18. Governing Law and Disputes",
      paragraphs: [
        "These Terms are governed by the laws of the Republic of South Africa, without regard to conflict-of-law principles.",
        "Except where applicable law provides otherwise, the courts located in Cape Town, Western Cape, South Africa will have exclusive jurisdiction over disputes arising out of or relating to these Terms or the service.",
        "If you are a consumer, you may have rights under mandatory local law that cannot be limited by these Terms.",
      ],
    },
    {
      heading: "19. Changes to These Terms",
      paragraphs: [
        "We may update these Terms from time to time. If we make material changes, we will post the updated Terms with a revised effective date and, where appropriate, provide additional notice.",
        "Your continued use of the service after updated Terms take effect means you accept the revised Terms.",
      ],
    },
    {
      heading: "20. Contact Us",
      paragraphs: [
        "Operator: Nathaniel James Markram",
        "Support or legal contact: hello@solsticediary.com",
        "Mailing address: 13 St James St, Audas Estate, Cape Town, 7130",
      ],
    },
  ],
};

const accountDeletionPage = {
  label: "Account and Data Deletion",
  title: "Solstice Diary Account and Data Deletion",
  metadata: [
    { label: "Effective date", value: "August 13, 2026" },
    { label: "App", value: "Solstice Diary" },
    { label: "Operator", value: "Nathaniel James Markram" },
    {
      label: "Deletion contact",
      value: "hello@solsticediary.com",
      href: "mailto:hello@solsticediary.com?subject=Solstice%20Diary%20account%20deletion%20request",
    },
  ],
  introParagraphs: [
    "This page explains how Solstice Diary users can request deletion of their account and associated data, or request deletion of specific data without deleting their account.",
    "For security, we need to confirm that the request is coming from the person who controls the Solstice Diary account email before we delete account data outside the app.",
  ],
  sections: [
    {
      heading: "1. Delete Specific Data in the App",
      paragraphs: [
        "You can delete some Solstice Diary data inside the app without deleting your account.",
      ],
      bullets: [
        "Open Solstice Diary and sign in to your account.",
        "Delete individual journal entries, saved conversations, events, goals, habit records, or other editable content from the relevant app screen.",
        "When you delete an entry, Solstice Diary is designed to also remove related derived memory and search records created from that entry.",
      ],
    },
    {
      heading: "2. Request Specific Data Deletion Outside the App",
      paragraphs: [
        "If you cannot delete specific data inside the app, email us from the email address used for your Solstice Diary account and describe the data you want deleted.",
      ],
      bullets: [
        "Send your request to hello@solsticediary.com.",
        'Use the subject line "Solstice Diary data deletion request".',
        "Describe the category of data you want deleted, such as journal entries, saved conversations, goals, events, moods, insights, reports, search records, or memory records.",
        "Do not include journal entries, private notes, passwords, or other sensitive content in the email.",
      ],
    },
    {
      heading: "3. Delete Your Account in the App",
      paragraphs: [
        "If you can still sign in to Solstice Diary, use the permanent account deletion flow inside the app. This is the fastest way to request deletion because it confirms that you are signed in to the account being deleted.",
      ],
      bullets: [
        "Open Solstice Diary and sign in to the account you want deleted.",
        "Go to the account or settings area.",
        "Choose the permanent account deletion option and follow the confirmation steps shown in the app.",
      ],
    },
    {
      heading: "4. Request Account Deletion Outside the App",
      paragraphs: [
        "If you cannot access the app, email us from the email address used for your Solstice Diary account.",
      ],
      bullets: [
        "Send your request to hello@solsticediary.com.",
        'Use the subject line "Solstice Diary account deletion request".',
        "Include the email address used for your Solstice Diary account if it is not already clear from your message.",
        "Do not include journal entries, private notes, passwords, or other sensitive content in the email.",
      ],
    },
    {
      heading: "5. How We Verify the Request",
      paragraphs: [
        "To protect your journal and account, we do not delete an account based only on a message from an unrelated email address.",
      ],
      bullets: [
        "The deletion request must come from the email address used for the Solstice Diary account.",
        'We will reply to that same email address and ask you to confirm with the phrase: "I confirm that I want to permanently delete my Solstice Diary account and associated data."',
        'For requests to delete specific data without deleting the account, we may ask you to confirm with the phrase: "I confirm that I want Solstice Diary to delete the specific data described in my request."',
        "We will only continue after receiving that confirmation reply from the same account email address.",
        "If you contact us from a different email address, we may ask you to send the request from the account email or complete the deletion inside the app after signing in.",
      ],
    },
    {
      heading: "6. Data We Delete",
      paragraphs: [
        "For an in-app account deletion, Solstice Diary begins removing the supported Solstice-controlled account data as part of the confirmed deletion flow. For a verified request handled outside the app, we will complete deletion of the requested Solstice-controlled active records within 30 days unless applicable law permits or requires a different period.",
      ],
      bullets: [
        "For account deletion requests: the core Firestore user document and its subcollections, including profile information, preferences, settings, onboarding state, journal entries, saved guided conversations, moods, goals, habit records, calendar events, and related app content.",
        "Saved insights, reports, summaries, and derived AI, search, memory, or index records stored inside the deleted Firestore account tree.",
        "The backend copy of the per-account content-encryption key.",
        "Pseudonymous per-user backend AI usage telemetry and rate-limit records that Solstice can associate with the deleted Firebase account.",
        "The Firebase Authentication account used to sign in to Solstice Diary.",
        "For specific data deletion requests: the data categories you ask us to delete, plus associated derived records where technically linked to that data.",
      ],
    },
    {
      heading: "7. Provider, Transaction, and Aggregate Records",
      paragraphs: [
        "Deleting a Solstice Diary account does not automatically cancel an Apple App Store or Google Play subscription. Cancel the subscription separately through the store account used to purchase it.",
        "Some records are processed by service providers and are not part of the Firestore account tree removed by the in-app function. Depending on the record and applicable law, a separate verified privacy request may be required and some records may need to be retained.",
      ],
      bullets: [
        "RevenueCat and the applicable app store may retain customer, entitlement, receipt, transaction, refund, tax, fraud-prevention, or subscription records under their own retention and legal obligations.",
        "Firebase Analytics and Firebase Crashlytics may retain installation-based analytics, events, errors, stack traces, device or app metadata, and provider identifiers according to Google's retention practices.",
        "OpenAI, Anthropic, and Cohere may retain request, safety, abuse-prevention, or service records according to the applicable service terms and retention settings.",
        "A Formspree website waitlist submission is separate from the app account. Ask us to remove the waitlist email or use an unsubscribe method in a communication if you also want that record removed.",
        "Aggregated operational statistics that no longer identify or relate back to your account may remain after account deletion.",
        "Minimal support records showing the request date, verification date, deletion completion date, and operator handling the request.",
        "Records we are legally required to keep, if any.",
      ],
    },
    {
      heading: "8. Backups and Device-Local Data",
      paragraphs: [
        "Residual copies may remain for a limited period in backups or recovery systems and are removed through normal backup-expiry processes. We do not promise a single deletion window for every provider backup or override records that must be retained by law.",
        "The in-app deletion cleanup is designed to cancel Solstice-managed local reminders and remove cached settings, onboarding state, and the locally cached encryption key. Some local drafts, operating-system notification history, shared exports, app-store records, or other device data may remain until you delete the app, clear its storage, or remove those records through the relevant device or service.",
      ],
    },
    {
      heading: "9. After Deletion",
      paragraphs: [
        "Account deletion is intended to be permanent. Once deletion is complete, your deleted account and associated journal data cannot be restored through Solstice Diary.",
        "Specific data deletion is also intended to be permanent for the data covered by the verified request, but it does not close your Solstice Diary account unless you separately request account deletion.",
        "For more detail about how Solstice Diary handles personal information, read the Solstice Diary Privacy Policy.",
      ],
    },
  ],
};

const legalPages = {
  "/privacy-policy": privacyPolicyPage,
  "/terms": termsOfServicePage,
  "/account-deletion": accountDeletionPage,
};

module.exports = legalPages;
